import {
  AllPointOfInterestsForDataLoaderDocument,
  BlisterHeightType,
  PointOfInterest,
  SelectedPointOfInterestDocument,
  useUpdateBlisterHeightTypeMutation,
  useUpdateCalculationMethodMutation,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Button, FormControlLabel, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import {
  CustomRadio,
  CustomRadioGroup,
  CustomSlider,
  CustomSwitch,
  LineBreak,
  OverrideSection,
  RadioLabel,
  Title,
} from './styles';
import { PageLoader } from '@/components/shared/PageLoader';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { heightTypeMapping } from '@/constants';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { DropDownField } from '@/components/shared/CustomField';

interface BlisterHeightOption {
  label: string;
  value: BlisterHeightType;
}

const BLISTER_HEIGHT_OPTIONS: BlisterHeightOption[] = [
  { label: heightTypeMapping[BlisterHeightType.Pdr], value: BlisterHeightType.Pdr },
  { label: heightTypeMapping[BlisterHeightType.BlisterTool], value: BlisterHeightType.BlisterTool },
  { label: heightTypeMapping[BlisterHeightType.Uncertain], value: BlisterHeightType.Uncertain },
];

export const BlisterHeightMethodSelector = ({
  pointOfInterest,
}: {
  pointOfInterest: PointOfInterest;
}) => {
  const structureId = useGetStructureId();
  const pointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const structureBlisterHeightType = useAtomValue(state.structureBlisterHeightType);
  const [showUncertainDialog, setShowUncertainDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [isOverrideEnabled, setIsOverrideEnabled] = useState(
    pointOfInterest.blister?.isManuallyOverridden || false
  );
  const [selectedMethod, setSelectedMethod] = useState<BlisterHeightType | null>(
    pointOfInterest.blister?.heightType ?? null
  );

  const [updateCalculationMethod, { loading: updateLoading }] =
    useUpdateCalculationMethodMutation();

  const [updateBlisterHeightType, { loading: updateBlisterHeightTypeLoading }] =
    useUpdateBlisterHeightTypeMutation();

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOverrideEnabled(event.target.checked);
    if (!event.target.checked) {
      setSelectedMethod(null);
    }
    handleUpdate({
      isManuallyOverridden: event.target.checked,
      heightType: selectedMethod ?? BlisterHeightType.BlisterTool,
    });
  };

  const template = useMemo(() => {
    return pointOfInterestTemplates.find((template) => template.id === pointOfInterest.templateId);
  }, [pointOfInterestTemplates, pointOfInterest.templateId]);

  const uncertainReasonField = useMemo(() => {
    return template?.fields.find((field) => field.key === 'uncertain_reason');
  }, [template]);

  const uncertainOptions = useMemo(() => {
    return uncertainReasonField?.options ?? [];
  }, [uncertainReasonField]);

  const handleMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === BlisterHeightType.Uncertain.toString() && uncertainReasonField) {
      setShowUncertainDialog(true);
    } else {
      setSelectedMethod(event.target.value as BlisterHeightType);
      handleUpdate({
        isManuallyOverridden: isOverrideEnabled,
        heightType: event.target.value as BlisterHeightType,
      });
    }
  };

  const loading = useMemo(
    () => updateLoading || updateBlisterHeightTypeLoading,
    [updateLoading, updateBlisterHeightTypeLoading]
  );

  const handleUpdate = useCallback(
    async ({
      heightType,
      isManuallyOverridden,
    }: {
      heightType: BlisterHeightType;
      isManuallyOverridden: boolean;
    }) => {
      if (loading) return;
      await updateCalculationMethod({
        variables: {
          input: {
            pointOfInterestId: pointOfInterest.id,
            isManuallyOverridden,
            heightType,
            lastModifiedFor: 'Override calculation method',
            uncertainFieldKey: uncertainReasonField?.key ?? '',
            ...(heightType === BlisterHeightType.Uncertain ? { uncertainReason: reason } : {}),
          },
        },
      });
      await updateBlisterHeightType({
        variables: {
          input: {
            pointOfInterestIds: [pointOfInterest.id],
            structureId,
            forceUpdate: true,
            ...(isManuallyOverridden ? { heightType } : { heightType: structureBlisterHeightType }),
          },
        },
        refetchQueries: [AllPointOfInterestsForDataLoaderDocument, SelectedPointOfInterestDocument],
      });
      setSelectedMethod(heightType);
    },
    [
      loading,
      updateCalculationMethod,
      pointOfInterest.id,
      reason,
      uncertainReasonField?.id,
      updateBlisterHeightType,
      structureId,
      structureBlisterHeightType,
    ]
  );

  const handleClose = () => {
    setShowUncertainDialog(false);
    setReason('');
  };

  const handleConfirm = () => {
    setShowUncertainDialog(false);
    handleUpdate({
      heightType: BlisterHeightType.Uncertain,
      isManuallyOverridden: true,
    });
  };

  return (
    <>
      <GenericDialog
        open={showUncertainDialog}
        dialogStyles={{
          '.MuiDialog-container': {
            alignItems: 'baseline',
            paddingTop: '14.8rem',
          },
        }}
        paperStyles={{
          maxWidth: '47.2rem',
        }}
        titleStyles={{
          padding: '2.4rem',
          fontSize: '1.6rem',
          fontWeight: 500,
        }}
        actionsStyles={{ mr: 2, pb: 2, pt: '1.4rem' }}
        onClose={handleClose}
        title="Mark it as uncertain?"
        content={
          <>
            <DropDownField
              mode="Edit"
              id={'uncertain_reason'}
              title={'Uncertain Reason'}
              sx={{ margin: '2rem 0' }}
              value={reason}
              options={uncertainOptions}
              disableClearable
              onFieldValueChange={(value) => setReason(value)}
            />
          </>
        }
        actions={[
          <Button key="cancel" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>,
          <Button key="confirm" variant="contained" disabled={!reason} onClick={handleConfirm}>
            Confirm
          </Button>,
        ]}
      />
      <Stack>
        <OverrideSection>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="space-between"
            width="100%"
          >
            <Title>Override calculation method</Title>

            <FormControlLabel
              control={
                <CustomSwitch
                  checked={isOverrideEnabled}
                  onChange={handleSwitchChange}
                  disabled={loading}
                  size="medium"
                />
              }
              label=""
              labelPlacement="start"
            />
          </Stack>
          <CustomSlider open={isOverrideEnabled}>
            {!loading && (
              <CustomRadioGroup value={selectedMethod || ''} onChange={handleMethodChange}>
                {BLISTER_HEIGHT_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<CustomRadio disableRipple />}
                    label={
                      <RadioLabel>
                        {`${option.label} ${
                          option.value === structureBlisterHeightType ? '(Default)' : ''
                        }`}
                      </RadioLabel>
                    }
                    disabled={loading}
                  />
                ))}
              </CustomRadioGroup>
            )}
            {loading && (
              <Stack width="100%" alignItems="center" justifyContent="center">
                <PageLoader variant="sm" flashing hideProgress />
              </Stack>
            )}
          </CustomSlider>
        </OverrideSection>
        <LineBreak />
      </Stack>
    </>
  );
};
